import _ from 'lodash';

import { NodeType } from '../../utils/types';
import Tree, { SELECT_MODE } from '../GraphTree/Tree';
import TreeFuncs, { updateExpandOnItems, selectItem, buildTree, NODE_FILTER } from '../GraphTree/TreeFuncs';
import React, { useCallback, useState, useContext, useMemo, useEffect } from 'react';
import useAlerts from '../../utils/hooks/useAlerts';
import useAPI from '../../services/useAPI';
import { useTreeRoots } from 'components/GraphTree/queries';
import useApiCall from '../../services/useApiCall';

import {
  Stack,
  StackItem
} from '@patternfly/react-core';
import TreeSelector from '../GraphTree/TreeSelector';
import PropTypes from 'prop-types';
import './NodeTreePage.css';
import NodeContext from '../../services/Context/NodeContext';
import './MinimalTreePage.css';


// Adds tree selection to page
const MinimalTreePage = ({
  onItemSelected,
  initialRoot: _initialRoot,
  initialExpandedPath,
  onTreeRootChange,
  allowedSelection = [NodeType.Asset, NodeType.Node],
  initialSelectedNodes = []
}) => {
  // TODO: use error state?
  const nodeContext = useContext(NodeContext);
  const [, setError] = useState(false);
  const [originalTree, setOriginalTree] = useState(null);
  const [initialRoot, setInitialRoot] = useState(_initialRoot);
  const [alerts, , addErrorAlert] = useAlerts();
  const [tree, setTree] = useState([]);

  const api = useAPI();
  const [treeRoots, isLoadingRoots, refreshList] = useTreeRoots({ onError: addErrorAlert });

  // TODO: move fetch to customHook and add some of the processing from graphtree as well... (perhaps most?)

  const [getTree, isLoadingTree] = useApiCall({
    apiCall: api.core.getTreeFromNode,
    onSuccess: useCallback((result) => {
      setOriginalTree(buildTree(result, []));
    }, []),
    onError: useCallback(() => setError(true), [])
  }, []);

  useEffect(() => {
    if (isLoadingTree || _.isEmpty(originalTree)) {
      return;
    }
    const _tree = _.cloneDeep(originalTree);
    if (initialExpandedPath && !_.isEmpty(_tree)) {
      setTree(TreeFuncs.map(updateExpandOnItems([...initialExpandedPath]))(_tree));
    } else {
      setTree(_tree);
    }
  }, [initialExpandedPath, isLoadingTree, originalTree]);

  const _tree = useMemo(() => {
    if (tree == null || _.isEmpty(tree)) {
      return [];
    }

    return [{ ...TreeFuncs.map(selectItem(initialSelectedNodes))(_.cloneDeep(tree)) }];
  }, [tree, initialSelectedNodes]);

  const _treeRoots = useMemo(() => treeRoots ?? {}, [treeRoots]);

  useEffect(() => {
    if (_.isEmpty(_treeRoots)) {
      return;
    }
    const selectedRoot = nodeContext?.lastTreeRoot;
    const selectedTreeRoot = selectedRoot ? _.find(_treeRoots, { id: selectedRoot.id }) : null;
    const defaultRoot = selectedTreeRoot?.id ?? _treeRoots[0].id;


    setInitialRoot(currentInitialRoot => {
      const newRoot = currentInitialRoot ?? defaultRoot;
      getTree(newRoot);
      return newRoot;
    });
  }, [_treeRoots, getTree, nodeContext?.lastTreeRoot]);

  const _treeSelectItem = useCallback((items) => {
    onItemSelected?.(items);
  }, [onItemSelected]);


  const selectTree = useCallback((selectedTreeId) => {
    getTree(selectedTreeId);
    if (!onTreeRootChange) { // these should be updated through props if there is a connected function
      setInitialRoot(selectedTreeId);
    }
    onTreeRootChange?.(selectedTreeId);
    setOriginalTree([]);
    setTree([]);
  }, [getTree, onTreeRootChange]);

  return (
    <Stack>
      { alerts }
      <StackItem>
        <TreeSelector
          treeRoots={treeRoots}
          initialRoot={initialRoot}
          isLoading={isLoadingRoots}
          onSelect={selectTree}
          onRefresh={refreshList}
        />
      </StackItem>
      <StackItem isFilled className={'tree-view'}>
        <Tree
          tree={_tree}
          activeItems={initialSelectedNodes}
          onSelect={_treeSelectItem}
          isLoading={isLoadingTree}
          nodeFilter={NODE_FILTER}
          selectMode={SELECT_MODE.MULTI}
          allowedSelection={allowedSelection}
          onError={addErrorAlert}
        />
      </StackItem>
    </Stack>
  );
};

MinimalTreePage.propTypes = {
  onItemSelected: PropTypes.func,
  initialExpandedPath: PropTypes.array,
  initialRoot: PropTypes.string,
  initialSelectedNodes: PropTypes.array
  /**
   * Will send the current item that is first going to be selected
   */
};

export default React.memo(MinimalTreePage);
